import { Audience, Prequalification } from '@shared/types/database'
import * as graph from '@shared/types/graphql'
import preQualificationTypes from '@/utilities/pq-types'
import preQualificationInternational from '@/assets/json/pqInternational.json'
import { format, blurb } from './audience-helpers.js'
import { intersection } from 'lodash-es'
import { AudienceCriteriaType } from '@/components/AudienceBuilder/Audience'

const pqTypes = Object.assign(preQualificationTypes, preQualificationInternational)
const qualificationMap = Object.values(pqTypes).reduce((accum: Record<string, Prequalification>, qual: any) => {
  accum[qual.lucidKey] = qual as Prequalification
  return accum
}, {} as Record<string, Prequalification>)

export const USAdultsAudience = (workspaceAudiences: Pick<graph.Audience, '_id' | 'payload'>[]) => {
  // formatted for builder
  const audiences = workspaceAudiences.filter((a) => { return a.payload.account_id === 'alpha_featured' && a.payload.name === 'US Adults' })
  if (audiences.length === 1) {
    return format.forBuilder(audiences[0]._id, audiences[0].payload)
  }

  return USAdultsAudienceJSON()
}

export const companyAudiences = (workspaceAudiences: graph.Audience[]) => {
  return workspaceAudiences
    .filter((a) => { return a.payload.account_id !== 'alpha_featured' && a.payload.name && isVisible(a) && !a.payload.archived })
    .map((a) => { return format.forBuilder(a._id, a.payload) })
    .sort((a, b) => { return a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1 })
}

const isVisible = (audience: graph.Audience):boolean => {
  // By default all audiences are visible if we haven't updated this flag yet
  if (!Object.keys(audience.payload.segment).includes('visible') || audience.payload.segment.visible) {
    return true
  } else {
    return false
  }
}

export const featuredAudiences = (workspaceAudiences: graph.Audience[]) => {
  const audiences = workspaceAudiences
    .filter((a) => { return a.payload.account_id === 'alpha_featured' && a.payload.name })
    .map((a) => { return format.forBuilder(a._id, a.payload) })
    .sort((a, b) => {
      if (a.title === 'US Adults') return -1
      if (b.title === 'US Adults') return 1
      return a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1
    })

  // Add US Adults Audience if it doesn't already exist as a featured Audience.
  if (!audiences.some(a => a.title === 'US Adults')) {
    return [USAdultsAudienceJSON(), ...audiences]
  }

  return audiences
}

export const USAdultsAudienceJSON = () => {
  return {
    title: 'US Adults',
    criteria: {
      Age: ['18', '75'],
      International: undefined,
      'Additional Criteria': undefined
    },
    id: '5da633a1d604b500042e1fb1',
    segid: 'gp',
    demoQualifies: true,
    accountId: 'alpha_featured'
  }
}

export const processAudience = (audience: Pick<graph.Audience, '_id' | 'payload'>) : Partial<Audience> => {
  const criteria: Record<string, any> = {}
  for (const key in audience.payload.segment.lucid) {
    const newKey = qualificationMap[key].name
    criteria[newKey] = audience.payload.segment.lucid[key]
  }

  if (audience.payload.segment.international && audience.payload.segment.countries) {
    criteria.International = audience.payload.segment.countries
  }

  if (audience.payload.segment.custom) {
    criteria['Additional Criteria'] = audience.payload.segment.custom
  }

  return {
    title: audience.payload.name,
    id: audience._id,
    legacy: false,
    criteria,
    accountId: audience.payload.account_id,
    demoQualifies: audience.payload.segment.demoQualifies,
    segid: audience.payload.segId
  }
}

export const criteriaOrdering = [
  'Age',
  'Education',
  'Gender',
  'Household Income',
  'State',
  'Parental Status',
  'Relationship Status',
  'Household Type',
  'Employment',
  'Industry',
  '# of Employees',
  'Company Dept.',
  'Job Title',
  'B2B Decisions',
  'International',
  'Device',
  'Big Purchases',
  'Additional Criteria'
]

export const getCriteriaTypeTitle = (criteriaType: AudienceCriteriaType): string => {
  if (criteriaType === AudienceCriteriaType.ADDITIONAL) {
    return 'Custom criteria'
  }
  return criteriaType
}

export const translateCriteria = (demographics: Record<string, any>) => {
  const orderedCriteria = intersection(criteriaOrdering, Object.keys(demographics)) as AudienceCriteriaType[]
  return orderedCriteria.map((criteria) => {
    let value
    if (['Household Income', 'Age'].includes(criteria)) {
      value = display(criteria, demographics[criteria])
    } else if (isCustom(criteria)) {
      value = demographics[criteria]
    } else {
      value = translate(criteria, demographics[criteria])
    }

    return {
      name: getCriteriaTypeTitle(criteria),
      value
    }
  })
}

const isCustom = (criteria: string) => {
  return criteria === 'Additional Criteria'
}

const display = (criteria: string, range: number[]) => {
  return criteria === 'Age' ? blurb.displayAge(range) : blurb.displayHHI(range)
}

const translate = (criteria: string, demographic: string) => {
  return blurb.displayTextFromPreCodes(criteria, demographic)
}

export function formattedAgeRange (audience: Partial<Audience>): string {
  const translatedCriteria = translateCriteria(audience.criteria ?? {})
  const criteria = translatedCriteria.find(crit => crit.name === 'Age')
  if (criteria) {
    return criteria.value
  } else {
    return display('Age', [18, 75])
  }
}

export function formattedGeographicLocation (audience: Partial<Audience>): string {
  const translatedCriteria = translateCriteria(audience.criteria ?? {})
  const internationalCriteria = translatedCriteria.find(crit => crit.name === 'International')
  if (internationalCriteria) {
    return `International, ${internationalCriteria.value}`
  }
  const stateCriteria = translatedCriteria.find(crit => crit.name === 'State')
  if (stateCriteria) {
    return `${stateCriteria.value}`
  }
  return 'USA'
}

export function formattedAudienceName (audience: Partial<Audience>): string {
  return audience.label ?? audience.title ?? 'N/A'
}
